import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Save } from 'lucide-react';
import { AlertMessage } from '../../SSOConfigPage/components';
import { EmailPasswordSection, GoogleOAuthSection, GoogleSAMLSection, MicrosoftOAuthSection, MicrosoftSAMLSection } from '../../SSOConfigPage/sections';
export function SSOSettings({ config, secrets, loading, error, success, authServiceUrl, tenantSlug, onConfigChange, onSecretsChange, onSave, isUploadingXML, uploadError, onXMLUpload }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-lg font-medium text-foreground mb-2",
                        children: "Single Sign-On Configuration"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Configure authentication providers and SSO settings for your organization."
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx(AlertMessage, {
                variant: "error",
                children: error
            }),
            success && /*#__PURE__*/ _jsx(AlertMessage, {
                variant: "success",
                children: success
            }),
            uploadError && /*#__PURE__*/ _jsx(AlertMessage, {
                variant: "error",
                children: uploadError
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-6",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "bg-card rounded-lg border border-border p-6",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "flex items-start justify-between",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-lg font-semibold text-foreground mb-1",
                                        children: "📁 Upload Microsoft SAML XML"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-muted-foreground mb-4",
                                        children: "Quick setup: Upload your Federation Metadata XML from Azure AD to automatically configure Microsoft SAML"
                                    }),
                                    /*#__PURE__*/ _jsx("label", {
                                        htmlFor: "microsoft-metadata-upload",
                                        className: `inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium cursor-pointer transition-colors ${isUploadingXML ? 'bg-muted text-muted-foreground border-input cursor-not-allowed' : 'bg-primary text-primary-foreground border-primary hover:bg-primary/90'}`,
                                        children: isUploadingXML ? 'Uploading...' : 'Choose XML File'
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        id: "microsoft-metadata-upload",
                                        type: "file",
                                        accept: ".xml,text/xml,application/xml",
                                        onChange: onXMLUpload,
                                        disabled: isUploadingXML,
                                        className: "hidden"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mt-3 text-xs text-muted-foreground",
                                        children: "Download from: Azure Portal → Enterprise Applications → Single sign-on → SAML Certificates → Federation Metadata XML"
                                    })
                                ]
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(EmailPasswordSection, {
                        enabled: config.email_password_enabled,
                        onToggle: (enabled)=>onConfigChange({
                                ...config,
                                email_password_enabled: enabled
                            })
                    }),
                    /*#__PURE__*/ _jsx(GoogleOAuthSection, {
                        enabled: config.google_enabled,
                        clientId: config.google_client_id,
                        clientSecret: secrets.google_client_secret,
                        onToggle: (enabled)=>onConfigChange({
                                ...config,
                                google_enabled: enabled
                            }),
                        onClientIdChange: (value)=>onConfigChange({
                                ...config,
                                google_client_id: value
                            }),
                        onClientSecretChange: (value)=>onSecretsChange({
                                ...secrets,
                                google_client_secret: value
                            })
                    }),
                    /*#__PURE__*/ _jsx(MicrosoftOAuthSection, {
                        enabled: config.microsoft_enabled,
                        clientId: config.microsoft_client_id,
                        clientSecret: secrets.microsoft_client_secret,
                        tenantId: config.microsoft_tenant_id,
                        onToggle: (enabled)=>onConfigChange({
                                ...config,
                                microsoft_enabled: enabled
                            }),
                        onClientIdChange: (value)=>onConfigChange({
                                ...config,
                                microsoft_client_id: value
                            }),
                        onClientSecretChange: (value)=>onSecretsChange({
                                ...secrets,
                                microsoft_client_secret: value
                            }),
                        onTenantIdChange: (value)=>onConfigChange({
                                ...config,
                                microsoft_tenant_id: value
                            })
                    }),
                    /*#__PURE__*/ _jsx(GoogleSAMLSection, {
                        enabled: config.google_saml_enabled ?? false,
                        entityId: config.google_saml_entity_id ?? '',
                        ssoUrl: config.google_saml_sso_url ?? '',
                        certificate: config.google_saml_certificate ?? '',
                        emailAttribute: config.google_saml_email_attribute ?? '',
                        authServiceUrl: authServiceUrl,
                        tenantSlug: tenantSlug,
                        onToggle: (enabled)=>onConfigChange({
                                ...config,
                                google_saml_enabled: enabled
                            }),
                        onEntityIdChange: (value)=>onConfigChange({
                                ...config,
                                google_saml_entity_id: value
                            }),
                        onSsoUrlChange: (value)=>onConfigChange({
                                ...config,
                                google_saml_sso_url: value
                            }),
                        onCertificateChange: (value)=>onConfigChange({
                                ...config,
                                google_saml_certificate: value
                            }),
                        onEmailAttributeChange: (value)=>onConfigChange({
                                ...config,
                                google_saml_email_attribute: value
                            })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        id: "microsoft-saml-section",
                        children: /*#__PURE__*/ _jsx(MicrosoftSAMLSection, {
                            enabled: config.microsoft_saml_enabled ?? false,
                            entityId: config.microsoft_saml_entity_id ?? '',
                            ssoUrl: config.microsoft_saml_sso_url ?? '',
                            certificate: config.microsoft_saml_certificate ?? '',
                            emailAttribute: config.microsoft_saml_email_attribute ?? '',
                            authServiceUrl: authServiceUrl,
                            tenantSlug: tenantSlug,
                            onToggle: (enabled)=>onConfigChange({
                                    ...config,
                                    microsoft_saml_enabled: enabled
                                }),
                            onEntityIdChange: (value)=>onConfigChange({
                                    ...config,
                                    microsoft_saml_entity_id: value
                                }),
                            onSsoUrlChange: (value)=>onConfigChange({
                                    ...config,
                                    microsoft_saml_sso_url: value
                                }),
                            onCertificateChange: (value)=>onConfigChange({
                                    ...config,
                                    microsoft_saml_certificate: value
                                }),
                            onEmailAttributeChange: (value)=>onConfigChange({
                                    ...config,
                                    microsoft_saml_email_attribute: value
                                })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "bg-secondary border border-primary rounded-lg p-4",
                children: [
                    /*#__PURE__*/ _jsx("h5", {
                        className: "text-sm font-medium text-primary mb-2",
                        children: "About SSO Configuration"
                    }),
                    /*#__PURE__*/ _jsxs("ul", {
                        className: "text-sm text-primary space-y-1 list-disc list-inside",
                        children: [
                            /*#__PURE__*/ _jsx("li", {
                                children: "Enable one or more authentication methods for your organization"
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "Email/Password authentication is available by default"
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "OAuth and SAML configurations require credentials from your provider"
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "Changes take effect immediately"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end pt-4 border-t border-border",
                children: /*#__PURE__*/ _jsxs("button", {
                    onClick: onSave,
                    disabled: loading,
                    className: "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-foreground bg-primary hover:bg-primary/90 disabled:bg-primary disabled:cursor-not-allowed transition-colors",
                    children: [
                        /*#__PURE__*/ _jsx(Save, {
                            className: "w-4 h-4 mr-2"
                        }),
                        loading ? 'Saving...' : 'Save SSO Configuration'
                    ]
                })
            })
        ]
    });
}
